import React, { useEffect, useMemo, useState } from 'react';
import {
  SliceOrderRate,
  updateDriverPayConfirmationModal
} from '../../redux/features/orders/ordersSlice';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import {
  getInvoiceServiceUnitLabel,
  InvoiceService,
  InvoiceServiceUnit
} from '../../redux/api/invoiceService/invoiceService.model';
import NumberUtils from '../../utils/numberUtils';
import useCalculateLoadedMiles from '../../hooks/useCalculateLoadedMiles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import DisabledBox from './DisabledBox';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import { IconButton, styled, Typography } from '@mui/material';
import { ReactComponent as CalculatorIcon } from './../../assets/icons/calculator.svg';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../../providers/Auth/Auth.provider';
import TwTooltip from '../TwTooltip/TwTooltip';
import { useGetTenantRoutingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import { PROVIDER } from '../../providers/User/User.model';
import InvoiceUtils from '../../utils/invoiceUtils';
import useCheckDriverPay from '../../hooks/useCheckDriverPay';
import { EntityType } from '../../redux/Global.model';
import { useAppDispatch } from '../../redux/hooks';
import {
  useGetQBOClassesByDivisionQuery,
  useGetQBOClassTrackingQuery
} from '../../redux/api/divisions/divisionsApi';
import { IDivisionQBOClassesResponse } from '../../redux/api/divisions/divisions.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';

export type RowData = SliceOrderRate & {
  amount?: number;
  description?: string;
  initial_service_option?: TwAutocompleteOption;
  invoice_service?: InvoiceService;
};

interface TwAdvancedRateRowProps {
  id: string;
  index: number;
  data: RowData;
  services: TwAutocompleteOption[];
  isFirstRender?: boolean;
  isMobile?: boolean;
  showDescription?: boolean;
  allowDeleteAll?: boolean;
  totalLoadedMiles?: number;
  recalculateTotalLoadedMiles?: boolean;
  hasInitialData?: boolean;
  setHasInitialData?: React.Dispatch<React.SetStateAction<boolean>>;
  errors: Record<string, unknown>;
  onRateUpdate: (id: string, newData: RowData, resetDriverPayOverrides: boolean) => void;
  onAddServiceClick?: (rowId: string) => void;
  onDeleteIconClick?: (id: string) => void;
  disabled?: boolean;
  quickbooksEnabled?: boolean;
  onClearFormErrorById?: (id: string) => void;
  defaultInvoiceServiceId?: string | null;
  amountOfDefaultService?: number;
  isLoading?: boolean;
  divisionId: string | null;
  hideMileageCalculator?: boolean;
  hideQboClass?: boolean;
}

const RowLabel = styled('label')(() => ({
  fontFamily: 'Poppins',
  fontSize: '14px',
  color: '#6B7280',
  textTransform: 'capitalize',
  fontWeight: 400,
  lineHeight: '20px'
}));

const defaultLoadRateUnitAutocompleteOptions: TwAutocompleteOption[] = Object.entries(
  InvoiceServiceUnit
).map(([key, value]) => ({
  id: key,
  label: getInvoiceServiceUnitLabel(value),
  value: key,
  tooltipTitle:
    value === InvoiceServiceUnit.PERCENTAGE ? 'Calculated based on the linehaul amount' : undefined
}));

const TwAdvancedRateRow: React.FC<TwAdvancedRateRowProps> = ({
  id,
  index,
  data,
  services,
  isFirstRender,
  isMobile,
  showDescription,
  allowDeleteAll,
  totalLoadedMiles,
  recalculateTotalLoadedMiles = true,
  hasInitialData,
  errors,
  onRateUpdate: onUpdate,
  onAddServiceClick,
  onDeleteIconClick,
  disabled,
  quickbooksEnabled,
  onClearFormErrorById,
  defaultInvoiceServiceId,
  amountOfDefaultService,
  isLoading,
  divisionId,
  hideMileageCalculator,
  hideQboClass = false
}) => {
  const { getTenantSetting } = useAuth();
  const { enableHideGoogleMapsAndRoutingCalculations } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const showServiceId = getTenantSetting('enable-service-id');
  const isMileageAutoCalculationDisabled = getTenantSetting('disable-auto-calculate-mileage');
  const [selectedInvoiceService, setSelectedInvoiceService] = useState<
    TwAutocompleteOption | null | undefined
  >(data.initial_service_option || null);

  const anyDriverPayExists = useCheckDriverPay({ entity: EntityType.ORDER });

  const { data: qboPreferences = { classTrackingPerTxn: false, classTrackingPerTxnLine: false } } =
    useGetQBOClassTrackingQuery(divisionId || '', { skip: !divisionId || !quickbooksEnabled });

  const { data: qboClasses = { content: [], size: 0 } } = useGetQBOClassesByDivisionQuery(
    divisionId || '',
    {
      skip: !divisionId || !quickbooksEnabled,
      selectFromResult: ({ data = { data: [], size: 0 } }) => ({
        data: {
          content: data.data.map((c) => ({
            id: c.Id,
            label: c.Name,
            value: c.Id
          })),
          size: data.size
        }
      })
    }
  );

  /**
   * it's necessary to check if the rate is a percentage and if it's a qbo service
    or invoice service in order to calculate the rate correctly
    if is a qbo service, the rate is decimal amount and it's not necessary to divide by 100
    if is an invoice service, the rate is a percentage and it's necessary to divide by 100
   * @param rate 
   * @param quantity 
   * @param unit 
   * @returns 
   */
  const getAmount = (
    rate: number,
    quantity: number,
    unit?: InvoiceServiceUnit | string | null,
    qbo_service_id?: string,
    invoice_service_id?: string
  ): number => {
    const isQboService = qbo_service_id && qbo_service_id !== '';
    const isInvoiceService = invoice_service_id && invoice_service_id !== '';

    if (unit && unit === InvoiceServiceUnit.FLAT) {
      return rate || 0;
    }

    if (unit && unit === InvoiceServiceUnit.PERCENTAGE && !isQboService && isInvoiceService) {
      // calculate the amount with precision to see to 4 decimal points on the row level
      return NumberUtils.calculateCurrencyAmountWithPrecision(rate / 100, quantity);
    }

    // calculate the amount with precision to see to 4 decimal points on the row level
    return NumberUtils.calculateCurrencyAmountWithPrecision(rate, quantity);
  };

  const { calculateTotalLoadedMiles } = useCalculateLoadedMiles();

  const [rateError, setRateError] = useState<boolean>(false);
  const [qtyError, setQtyError] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [serviceId, setServiceId] = useState<string>('-');
  const [qtyTooltip, setQtyTooltip] = useState<string>('Insert Google Miles');
  const [rateString, setRateString] = useState<string>((data.rate ?? '').toString()); // we store the rate as a string to avoid precision issues

  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);

  useEffect(() => {
    setQtyTooltip(
      `Insert ${routingSettings?.provider === PROVIDER.PC ? 'PC Miler' : 'Google'} Miles`
    );
  }, [routingSettings]);

  useEffect(() => {
    const currentService = services.find((service) => {
      return service.id === data.invoice_service_id;
    })?.value as InvoiceService;
    if (currentService) setServiceId(currentService.external_service_id ?? '-');
  }, [services, data.service_name]);

  useEffect(() => {
    setSelectedInvoiceService(data.initial_service_option || null);
  }, [data.initial_service_option]);

  const onRateUpdate = (id: string, newData: RowData, resetDriverPayOverrides?: boolean): void => {
    const amount = getAmount(
      newData?.rate || 0,
      newData?.quantity || 0,
      newData?.unit,
      newData.qbo_service_id,
      newData.invoice_service_id
    );

    setAmount(amount);
    onUpdate(
      id,
      { ...newData, amount },
      resetDriverPayOverrides === undefined || resetDriverPayOverrides
    );
  };

  const handleRowBlur = (): void => {
    setRateString((data.rate ?? '').toString());

    if (data.unit === InvoiceServiceUnit.FLAT && data?.rate === null && !rateError) {
      setRateError(true);
    } else {
      setRateError(false);
    }
  };

  const handleCalculatorIconClick = async (): Promise<void> => {
    const calculatedTotalLoadedMiles = await calculateTotalLoadedMiles();
    onRateUpdate(id, {
      ...data,
      quantity: calculatedTotalLoadedMiles
        ? NumberUtils.roundNumber(calculatedTotalLoadedMiles, 2)
        : 0
    });
    if (qtyError) setQtyError(false);
  };

  /**
   *   Rates can be negative, positive, or zero and should support up to the 4th decimal place.
   * */
  const handleInputRateChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    let value = e.target.value;
    const regex = /^-?[0-9]*\.?[0-9]{0,4}$/;

    if (value.startsWith('.')) {
      value = `0${value}`;
    }

    if (regex.test(value) && !/[eE]/.test(value)) {
      onRateUpdate(id, { ...data, rate: Number(value) });
      setRateString(value);
    }

    setRateError(false);
    onClearFormErrorById && onClearFormErrorById(`rate-${id}`);

    if (value === '') {
      onRateUpdate(id, { ...data, rate: undefined });
      setRateString('');

      if (rateError) setRateError(false);

      return;
    }
  };

  const handleServiceChange = (e: any, newValue: TwAutocompleteOption | null): void => {
    if (!newValue) {
      setSelectedInvoiceService(null);
      onRateUpdate(id, {
        ...data,
        invoice_service_id: undefined,
        qbo_service_id: undefined,
        qbo_service_type: undefined,
        service_name: undefined,
        unit: undefined,
        rate: 0,
        quantity: undefined
      });

      return;
    }

    const service = newValue.value as InvoiceService;
    setSelectedInvoiceService(newValue);

    onRateUpdate(id, {
      ...data,
      invoice_service_id: !quickbooksEnabled ? newValue.id : undefined,
      qbo_service_id: quickbooksEnabled ? newValue.id : undefined,
      qbo_service_type: quickbooksEnabled ? (newValue.value as InvoiceService).type : undefined,
      service_name: newValue.label,
      unit: quickbooksEnabled && service.isDefault ? InvoiceServiceUnit.FLAT : service.unit || '',
      rate: service.default_rate || data?.rate || 0,
      quantity: InvoiceUtils.getQuantityBasedOnUnitAndInvoiceSource({
        service,
        totalLoadedMiles: totalLoadedMiles || 0,
        amountOfDefaultService: amountOfDefaultService || 0,
        isQboEnabled: quickbooksEnabled || false
      })
    });
  };
  useEffect(() => {
    setRateError(data.has_rate_error || false);
  }, [data.has_rate_error]);

  useEffect(() => {
    if (data.amount) {
      setAmount(data.amount);
    }
  }, [data]);

  useEffect(() => {
    if (
      !quickbooksEnabled &&
      data.unit === InvoiceServiceUnit.PERCENTAGE &&
      !defaultInvoiceServiceId &&
      isLoading === false
    ) {
      addNotification({
        message:
          'Linehaul service is not yet defined. Define your linehaul service in the Invoice and Accounting settings.',
        variant: 'error'
      });
    }
  }, [data.unit]);

  // This is for detecting when a location change (maybe there is a better way to do this)
  useEffect(() => {
    if (recalculateTotalLoadedMiles === false) {
      return;
    }
    if (data.unit === InvoiceServiceUnit.PER_MILE && !isFirstRender && !hasInitialData) {
      onRateUpdate(id, { ...data, quantity: undefined });
      setQtyError(true);
    } else {
      const qty = !data.unit
        ? undefined
        : totalLoadedMiles && data.unit === InvoiceServiceUnit.PER_MILE
        ? NumberUtils.roundNumber(totalLoadedMiles, 2)
        : 0;

      onRateUpdate(
        id,
        {
          ...data,
          quantity: data.quantity || qty
        },
        false
      );
      setQtyError(false);
    }
  }, [totalLoadedMiles]);

  /**
   * Get the quantity based on the unit value,
   * if the unit is percentage, we need to get the amount of the default service
   * if the unit is per mile, we need to get the total loaded miles
   * if the unit is flat, we need to return 1
   * if the unit is not defined, we need to return the current quantity
   *
   * @param unit
   * @param currentQuantity
   * @returns
   */
  function getQuantityBasedOnUnitValue(
    unit: InvoiceServiceUnit,
    currentQuantity?: number
  ): number | undefined {
    if (unit === InvoiceServiceUnit.FLAT) {
      return 1;
    }

    if (unit === InvoiceServiceUnit.PER_MILE) {
      return totalLoadedMiles || 0;
    }

    if (unit === InvoiceServiceUnit.PERCENTAGE) {
      return amountOfDefaultService || 0;
    }

    return currentQuantity;
  }

  /**
   * Quickbooks rates are stored as integers, so when unit is percentage, we need to transform the rate to percentage
   * */
  function getRateValue(
    rate: number | null | undefined,
    unit: string | null | undefined
  ): number | undefined {
    const unitValue = unit as InvoiceServiceUnit;

    if (!rate) {
      return undefined;
    }

    if (quickbooksEnabled && rate && unitValue === InvoiceServiceUnit.PERCENTAGE) {
      return rate / 100;
    }

    return rate;
  }

  const selectedQboClass = useMemo(() => {
    if (!data.qbo_attributes?.qbo_class_ref || !qboClasses?.content) {
      return undefined;
    }
    return qboClasses.content.find(
      (qboClass) => qboClass.id === data.qbo_attributes?.qbo_class_ref?.value
    );
  }, [data.qbo_attributes?.qbo_class_ref, qboClasses]);

  return (
    <Box
      position="relative"
      borderBottom="1px solid #E5E7EB"
      marginBottom={isMobile ? '12px' : '8px'}
      paddingRight={isMobile ? '24px' : undefined}
      display={isMobile ? 'flex' : undefined}
      justifyContent={isMobile ? 'space-between' : undefined}
      onBlur={handleRowBlur}
    >
      <Grid container item columnSpacing={1}>
        <Grid
          item
          container
          xs={isMobile ? 12 : showServiceId ? 2.5 : 3}
          display="flex"
          marginBottom={isMobile ? '4px' : undefined}
        >
          {isMobile && (
            <Grid item xs={2.8} display="flex" alignItems="center">
              <RowLabel>Service</RowLabel>
            </Grid>
          )}
          <Grid item xs={isMobile ? 9 : 12}>
            {disabled ? (
              <DisabledBox value={selectedInvoiceService?.label || ''} />
            ) : (
              <TwAutocomplete
                placeholder="Please select"
                name={`service-${id}`}
                data-testid={`service-${index}-${id}`}
                optionList={services}
                value={selectedInvoiceService}
                sortList={false}
                error={
                  `service-${id}` in errors || `services[${index}].invoice_service_id` in errors
                }
                onChange={(e, newValue): void => {
                  if (anyDriverPayExists) {
                    e.stopPropagation();
                    dispatch(
                      updateDriverPayConfirmationModal({
                        open: true,
                        action: () => handleServiceChange(e, newValue)
                      })
                    );
                  } else {
                    handleServiceChange(e, newValue);
                  }
                }}
                listAction={
                  quickbooksEnabled
                    ? undefined
                    : {
                        label: 'New Service',
                        action: (): void => {
                          onAddServiceClick && onAddServiceClick(id);
                        }
                      }
                }
              />
            )}
          </Grid>
        </Grid>

        {!isMobile && showServiceId && (
          <Grid container item xs={1} display="flex">
            <Grid item xs={12}>
              <TwField
                id={`serviceId-${index}-${id}`}
                data-testid={`serviceId-${index}-${id}`}
                name={`serviceId-${id}`}
                inputType={TwInputTypes.text}
                disabled={true}
                value={serviceId}
              />
            </Grid>
          </Grid>
        )}

        {quickbooksEnabled && qboPreferences.classTrackingPerTxnLine === true && !hideQboClass && (
          <Grid
            item
            container
            xs={isMobile ? 12 : showServiceId ? 1.5 : 2}
            display="flex"
            marginBottom={isMobile ? '4px' : undefined}
          >
            {isMobile && (
              <Grid item xs={2.8} display="flex" alignItems="center">
                <RowLabel>Class</RowLabel>
              </Grid>
            )}
            <Grid item xs={isMobile ? 9 : 12}>
              {disabled ? (
                <DisabledBox value={selectedQboClass?.label || ''} />
              ) : (
                <TwAutocomplete
                  placeholder="Please select"
                  name={`qbo-class-${id}`}
                  data-testid={`qbo-class-${index}-${id}`}
                  optionList={qboClasses.content}
                  value={selectedQboClass || null}
                  sortList={false}
                  error={`qbo-class-${id}` in errors || `qbo-classes[${index}].classId` in errors}
                  onChange={(e, newValue): void => {
                    onRateUpdate(id, {
                      ...data,
                      qbo_attributes: newValue?.id
                        ? {
                            qbo_class_ref: {
                              value: newValue.id,
                              name: newValue.label
                            }
                          }
                        : null
                    });
                  }}
                />
              )}
            </Grid>
          </Grid>
        )}

        <Grid
          container
          item
          xs={isMobile ? 12 : 2}
          display="flex"
          marginBottom={isMobile ? '4px' : undefined}
        >
          {isMobile && (
            <Grid item xs={2.8} display="flex" alignItems="center">
              <RowLabel>{showServiceId ? 'Unit & ID' : 'Unit'}</RowLabel>
            </Grid>
          )}
          <Grid item xs={!isMobile ? 12 : showServiceId ? 4.4 : 9}>
            {disabled ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  boxShadow: '0px 1px 2px 0px #0000000D',
                  height: '40px',
                  padding: '8px 12px',
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              >
                {getInvoiceServiceUnitLabel(data.unit || '')}
              </Box>
            ) : (
              <TwAutocomplete
                placeholder="N/A"
                name={`unit-${id}`}
                id={`unit-${index}-${id}`}
                data-testid={`unit-${index}-${id}`}
                optionList={defaultLoadRateUnitAutocompleteOptions}
                value={
                  data.unit
                    ? defaultLoadRateUnitAutocompleteOptions.find((o) => o.id === data.unit)
                    : null
                }
                sortList={false}
                onChange={(e, newValue): void => {
                  if (newValue) {
                    const qty = getQuantityBasedOnUnitValue(
                      newValue.id as InvoiceServiceUnit,
                      data.quantity
                    );
                    const rate = getRateValue(data.rate, newValue.id);

                    if (anyDriverPayExists) {
                      e.stopPropagation();
                      dispatch(
                        updateDriverPayConfirmationModal({
                          open: true,
                          action: () =>
                            onRateUpdate(id, {
                              ...data,
                              unit: newValue.id as InvoiceServiceUnit,
                              quantity: qty,
                              rate: rate
                            })
                        })
                      );
                    } else {
                      onRateUpdate(id, {
                        ...data,
                        unit: newValue.id as InvoiceServiceUnit,
                        quantity: qty,
                        rate: rate
                      });
                    }
                  }
                }}
                error={`unit-${id}` in errors || `services[${index}].unit` in errors}
                tooltipTitle={
                  data.unit === InvoiceServiceUnit.PERCENTAGE
                    ? 'Calculated based on the linehaul amount defined under setting'
                    : ''
                }
              />
            )}
          </Grid>
          {isMobile && showServiceId && (
            <Grid item xs={4} marginLeft={'6px'}>
              <TwField
                id={`serviceId-${index}-${id}`}
                data-testid={`serviceId-${index}-${id}`}
                name={`serviceId-${id}`}
                inputType={TwInputTypes.text}
                disabled={true}
                value={serviceId}
              />
            </Grid>
          )}
        </Grid>

        {isMobile && (
          <>
            <Grid container item xs={12} marginBottom="4px">
              <Grid item xs={2.5} display="flex" alignItems="center" flex={1}>
                <RowLabel sx={{ minWidth: '74px' }}>
                  {showServiceId ? 'Qty & rate' : 'Rate'}
                </RowLabel>
              </Grid>
              <Grid item xs={4.7} sx={isMobile ? { paddingLeft: '10px' } : undefined}>
                {disabled ? (
                  <DisabledBox value={data.quantity || ''} />
                ) : (
                  <TwField
                    name={`qty-${id}`}
                    id={`qty-${index}-${id}`}
                    data-testid={`qty-${index}-${id}`}
                    inputType={TwInputTypes.number}
                    value={data.unit === InvoiceServiceUnit.FLAT ? '-' : data.quantity || ''}
                    disabled={data.unit === InvoiceServiceUnit.FLAT}
                    placeholder={data.unit === InvoiceServiceUnit.FLAT ? '-' : 'Qty'}
                    error={
                      qtyError || `qty-${id}` in errors || `services[${index}].quantity` in errors
                    }
                    onInputChange={(e): void => {
                      const value = e.target.value;
                      if (anyDriverPayExists) {
                        e.stopPropagation();
                        dispatch(
                          updateDriverPayConfirmationModal({
                            open: true,
                            action: () =>
                              onRateUpdate(id, {
                                ...data,
                                quantity: value ? Number(value) : undefined
                              })
                          })
                        );
                      } else {
                        onRateUpdate(id, {
                          ...data,
                          quantity: value ? Number(value) : undefined
                        });
                      }

                      if (qtyError) setQtyError(false);
                    }}
                    endAdorment={
                      !enableHideGoogleMapsAndRoutingCalculations ||
                      (data.unit === InvoiceServiceUnit.PER_MILE &&
                        !isMileageAutoCalculationDisabled &&
                        !hideMileageCalculator && (
                          <IconButton onClick={handleCalculatorIconClick}>
                            <CalculatorIcon fill="#6B7280" />
                          </IconButton>
                        ))
                    }
                  />
                )}
              </Grid>
              <Grid item xs={4.4} marginLeft={'6px'}>
                {disabled ? (
                  <DisabledBox value={data?.rate || ''} />
                ) : (
                  <TwTooltip title={data.tooltip && data.rate ? data.tooltip : ''} placement="top">
                    <Box component="div">
                      <TwField
                        id={`rate-${index}-${id}`}
                        data-testid={`rate-${index}-${id}`}
                        name={`rate-${id}`}
                        inputType={TwInputTypes.number}
                        value={rateString || ''}
                        error={
                          rateError || `rate-${id}` in errors || `services[${index}].rate` in errors
                        }
                        onInputChange={(e): void => {
                          if (anyDriverPayExists) {
                            e.stopPropagation();
                            dispatch(
                              updateDriverPayConfirmationModal({
                                open: true,
                                action: () => handleInputRateChange(e)
                              })
                            );
                          } else {
                            handleInputRateChange(e);
                          }
                        }}
                        startAdorment={
                          /** Render dollar symbol when unit is not PERCENTAGE or is percentage but is quickbooks enabled **/
                          data.unit !== InvoiceServiceUnit.PERCENTAGE || quickbooksEnabled ? (
                            <Typography
                              variant="body2"
                              fontWeight="400"
                              fontSize="14px"
                              color="#9CA3AF"
                              marginRight="4px"
                            >
                              $
                            </Typography>
                          ) : undefined
                        }
                        endAdorment={
                          /** Render percentage symbol when unit is PERCENTAGE and quickbooks is disabled **/
                          data.unit === InvoiceServiceUnit.PERCENTAGE && !quickbooksEnabled ? (
                            <Typography
                              variant="body2"
                              fontWeight="400"
                              fontSize="14px"
                              color="#9CA3AF"
                              marginLeft="4px"
                            >
                              %
                            </Typography>
                          ) : undefined
                        }
                      />
                    </Box>
                  </TwTooltip>
                )}
              </Grid>
            </Grid>

            <Typography
              variant="body2"
              fontWeight="400"
              fontSize="14px"
              color="#9CA3AF"
              margin="5px"
            >
              {'$ ' +
                ((data.amount && data.amount) === 0 || !data.amount
                  ? 0
                  : NumberUtils.getCurrency(data.amount, 4).replace('$', ''))}
            </Typography>
          </>
        )}

        {!isMobile && (
          <>
            <Grid item xs={1.5} display="flex" alignItems="center">
              {disabled ? (
                <DisabledBox value={data.quantity || ''} />
              ) : (
                <TwField
                  name={`qty-${id}`}
                  data-testid={`qty-${index}-${id}`}
                  inputType={TwInputTypes.number}
                  value={data.unit === InvoiceServiceUnit.FLAT ? '-' : data.quantity || ''}
                  disabled={data.unit === InvoiceServiceUnit.FLAT}
                  placeholder={data.unit === InvoiceServiceUnit.FLAT ? '-' : ''}
                  error={
                    qtyError || `qty-${id}` in errors || `services[${index}].quantity` in errors
                  }
                  onInputChange={(e): void => {
                    const value = e.target.value;
                    if (anyDriverPayExists) {
                      e.stopPropagation();
                      dispatch(
                        updateDriverPayConfirmationModal({
                          open: true,
                          action: () =>
                            onRateUpdate(id, {
                              ...data,
                              quantity: value ? Number(value) : undefined
                            })
                        })
                      );
                    } else {
                      onRateUpdate(id, {
                        ...data,
                        quantity: value ? Number(value) : undefined
                      });
                    }

                    if (qtyError) setQtyError(false);
                  }}
                  endAdorment={
                    data.unit === InvoiceServiceUnit.PER_MILE &&
                    !isMileageAutoCalculationDisabled &&
                    !hideMileageCalculator && (
                      <TwTooltip title={qtyTooltip} placement="top">
                        <IconButton onClick={handleCalculatorIconClick}>
                          <CalculatorIcon fill="#6B7280" />
                        </IconButton>
                      </TwTooltip>
                    )
                  }
                />
              )}
            </Grid>

            <Grid item xs={1.5}>
              {disabled ? (
                <DisabledBox value={rateString || ''} />
              ) : (
                <TwTooltip title={data.tooltip && data.rate ? data.tooltip : ''} placement="top">
                  <Box component="div">
                    <TwField
                      name={`rate-${id}`}
                      data-testid={`rate-${index}-${id}`}
                      id={`rate-${index}-${id}`}
                      inputType={TwInputTypes.number}
                      value={rateString || ''}
                      error={
                        rateError || `rate-${id}` in errors || `services[${index}].rate` in errors
                      }
                      onInputChange={(e): void => {
                        if (anyDriverPayExists) {
                          e.stopPropagation();
                          dispatch(
                            updateDriverPayConfirmationModal({
                              open: true,
                              action: () => handleInputRateChange(e)
                            })
                          );
                        } else {
                          handleInputRateChange(e);
                        }
                      }}
                      startAdorment={
                        /** Render dollar symbol when unit is not PERCENTAGE or is percentage but is quickbooks enabled **/
                        data.unit !== InvoiceServiceUnit.PERCENTAGE || quickbooksEnabled ? (
                          <Typography
                            variant="body2"
                            fontWeight="400"
                            fontSize="14px"
                            color="#9CA3AF"
                            marginRight="4px"
                          >
                            $
                          </Typography>
                        ) : undefined
                      }
                      endAdorment={
                        /** Render percentage symbol when unit is PERCENTAGE and quickbooks is disabled **/
                        data.unit === InvoiceServiceUnit.PERCENTAGE && !quickbooksEnabled ? (
                          <Typography
                            variant="body2"
                            fontWeight="400"
                            fontSize="14px"
                            color="#9CA3AF"
                            marginLeft="4px"
                          >
                            %
                          </Typography>
                        ) : undefined
                      }
                    />
                  </Box>
                </TwTooltip>
              )}
            </Grid>

            <Grid item xs={2} display="flex" alignItems="center">
              <Box fontSize="14px" marginTop="-5px" id={`subtotal-${index}`}>
                <Box component="span" color="#9CA3AF">
                  ${' '}
                </Box>
                {amount === 0 ? 0 : NumberUtils.getCurrency(amount, 4).replace('$', '')}
              </Box>
            </Grid>
          </>
        )}
      </Grid>

      {showDescription && (
        <Grid>
          {disabled ? (
            <DisabledBox value={data.description || ''} />
          ) : (
            <TwField
              id="description"
              name="description"
              placeholder="Description"
              inputType={TwInputTypes.textarea}
              value={data.description || ''}
              onInputChange={(e): void => {
                onRateUpdate(id, { ...data, description: e.target.value });
              }}
            />
          )}
        </Grid>
      )}

      {!disabled && (index !== 0 || allowDeleteAll) && (
        <IconButton
          size="small"
          onClick={(): void => {
            if (anyDriverPayExists) {
              dispatch(
                updateDriverPayConfirmationModal({
                  open: true,
                  action: () => {
                    onDeleteIconClick && onDeleteIconClick(id);
                  }
                })
              );
            } else {
              onDeleteIconClick && onDeleteIconClick(id);
            }
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: isMobile ? -8 : 0,
            fill: 'red',
            color: '#9090A6 !important'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default TwAdvancedRateRow;
